/**
 * WorkflowParallelGroup.tsx — Parallel item 组摘要 + 嵌套 item 行
 */
import { useCallback, useEffect, useMemo, useState, memo } from 'react';

import {
  computeParallelGroupMetrics,
  formatParallelGroupSummary,
  parallelGroupHasActiveWork,
} from '../../core/workflow/workflowParallelGroupMetrics';
import type { SessionTree } from '../../types/tree';
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import { WorkflowExpandTrigger } from './WorkflowExpandTrigger';
import { WorkflowStageContainer } from './WorkflowStageContainer';
import {
  workflowContainerNodeVisualEqual,
  workflowContainerViewPropsEqual,
} from './workflowContainerMemo';

export interface WorkflowParallelGroupProps {
  groupKey: string;
  items: WorkflowContainerRuntimeNode[];
  loopTreesBySessionId: Record<string, SessionTree>;
  depth?: number;
  virtualized?: boolean;
  virtualizeThreshold?: number;
  groupParallelTools?: boolean;
}

function resolveLoopTree(
  node: WorkflowContainerRuntimeNode,
  loopTreesBySessionId: Record<string, SessionTree>,
): SessionTree | undefined {
  if (!node.loopSessionId) return undefined;
  return loopTreesBySessionId[node.loopSessionId];
}

export function WorkflowParallelGroupInner({
  groupKey,
  items,
  loopTreesBySessionId,
  depth = 1,
  virtualized,
  virtualizeThreshold,
  groupParallelTools,
}: WorkflowParallelGroupProps) {
  const metrics = useMemo(() => computeParallelGroupMetrics(items), [items]);
  const summary = formatParallelGroupSummary(metrics);
  /** 原则 A · L2：容器 status + scoped loop 双轨（结构帧可能早于 agent） */
  const hasActiveWork = useMemo(
    () => parallelGroupHasActiveWork(items, loopTreesBySessionId),
    [items, loopTreesBySessionId],
  );
  const allSettled =
    metrics.total > 0
    && metrics.running === 0
    && metrics.completed + metrics.failed >= metrics.total;

  const [expanded, setExpanded] = useState(hasActiveWork);

  useEffect(() => {
    if (hasActiveWork) {
      setExpanded(true);
      return;
    }
    if (allSettled) {
      setExpanded(false);
    }
  }, [hasActiveWork, allSettled]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'o') return;
      setExpanded(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const renderItem = (index: number) => {
    const item = items[index]!;
    return (
      <WorkflowStageContainer
        key={item.container_id}
        node={item}
        loopTree={resolveLoopTree(item, loopTreesBySessionId)}
        depth={depth}
        virtualized={virtualized}
        virtualizeThreshold={virtualizeThreshold}
        groupParallelTools={groupParallelTools}
      />
    );
  };

  return (
    <div
      className="lax-workflow-parallel-group"
      data-testid={`lax-workflow-parallel-group-${groupKey}`}
    >
      <WorkflowExpandTrigger
        summary={summary}
        expandHint={
          !expanded && hasActiveWork
            ? '(click to expand agents)'
            : !expanded && !hasActiveWork
              ? '(ctrl+o to expand)'
              : null
        }
        onToggle={toggleExpanded}
        expanded={expanded}
        testId={`lax-workflow-parallel-group-summary-${groupKey}`}
        className="lax-workflow-parallel-group__summary"
      />

      {expanded ? (
        <div className="lax-workflow-parallel-list" data-testid="lax-workflow-parallel-list">
          {items.map((_, index) => renderItem(index))}
        </div>
      ) : null}
    </div>
  );
}

function workflowParallelGroupPropsEqual(
  prev: WorkflowParallelGroupProps,
  next: WorkflowParallelGroupProps,
): boolean {
  if (prev.groupKey !== next.groupKey) return false;
  if (!workflowContainerViewPropsEqual(prev, next)) return false;
  if (prev.items.length !== next.items.length) return false;
  for (let i = 0; i < prev.items.length; i += 1) {
    const prevItem = prev.items[i]!;
    const nextItem = next.items[i]!;
    if (!workflowContainerNodeVisualEqual(prevItem, nextItem)) return false;
    const loopSessionId = prevItem.loopSessionId;
    if (loopSessionId) {
      if (prev.loopTreesBySessionId[loopSessionId] !== next.loopTreesBySessionId[loopSessionId]) {
        return false;
      }
    }
  }
  return true;
}

export const WorkflowParallelGroup = memo(WorkflowParallelGroupInner, workflowParallelGroupPropsEqual);
